import React, { useCallback, useEffect, useMemo, useState } from 'react';
import UserActionsMenu from '../UserActionsMenu';
import { fillFor } from '../wall/wallTokens';
import messageService from '../../services/messageService';
import { T, RADIUS, BODY_FONT, formatTime, formatDay, portalStatus, previewText, callRecordView } from './messagesModel';
import { fmtWhen, summaryLine } from './plan/planModel';
import { fmtDur } from './albumModel';
import { CallIcon } from './CallRecordCard';
import { LockIcon } from './SecureLockSheet';

/** Where the sheet docks beside the thread instead of sliding over it. */
export const DOCK_MIN_WIDTH = 860;
export const SHEET_WIDTH = 400;

const IC = {
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21v-1a8 8 0 0 1 16 0v1',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM20 20l-4-4',
  bell: 'M6 16V11a6 6 0 0 1 12 0v5l2 2H4zM10 21h4',
  lock: 'M4 11h16v10H4zM8 11V7a4 4 0 0 1 8 0v4',
  eye: 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  block: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM7 12h10',
  flag: 'M5 21V4h12l-2 4 2 4H5',
  trash: 'M4 7h16M9 7V4h6v3M6 7l1 14h10l1-14',
  add: 'M10 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM3 21v-1a7 7 0 0 1 14 0v1M19 8v6M16 11h6',
  calendar: 'M3 5h18v16H3zM3 10h18M8 3v4M16 3v4M12 13v5M9.5 15.5h5',
};

function Icon({ d, size = 18, stroke = 2 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="square" style={{ flexShrink: 0 }}><path d={d} /></svg>;
}

const SECTION_LABEL = { fontSize: 10, fontWeight: 700, letterSpacing: '.12em', color: T.grey500 };
const pill = (on) => ({ height: 34, padding: '0 14px', borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, background: on ? T.ink : T.white, color: on ? T.white : T.ink, fontSize: 10, fontWeight: 700, letterSpacing: '.1em', cursor: 'pointer', transition: 'background-color 200ms, color 200ms' });

/**
 * The chat info side sheet (handoff Part A §6 + Part B's CALLS section).
 *
 * Docked as a 400px column right of the thread from 860px of pane width (the thread shrinks);
 * narrower it overlays from the right over a 35% scrim. One header (CHAT INFO · ×, or ← MEDIA,
 * LINKS + VOICE), a scrolling body: the person in the portal ring with their Vibe Check and
 * the quick actions · UP NEXT from this chat · MEDIA with a peek grid and VIEW ALL · ROOMS
 * YOU'VE SHARED · THEIR PORTAL when one is up · CALLS · THIS CHAT.
 *
 * Deliberately absent (anti-features): read receipts, a typing toggle, mutual friends,
 * "active X min ago".
 */
export default function ChatInfoSheet({
  threadId,
  peer,
  isGroup = false,
  isSecure = false,
  viewerUserId,
  messages = [],
  docked = false,
  plan = null,
  reel = null,
  onClose,
  onOpenProfile,
  onJumpTo,
  onOpenPlanPortal,
  /** Plan detection on this chat: allowed at all (never on a Secure chat), the switch, FIND A PLAN. */
  planAllowed = false,
  planEnabled = true,
  onTogglePlan = null,
  onFindPlan = null,
  onOpenAlbum,
  onOpenMessageMedia,
  /** The VOICE tab's SEARCH NOTES: the dock's whole-chat notes search (voice-notes handoff §4b). */
  onSearchVoiceNotes = null,
  onOpenReel,
  onToggleSecure,
  onAddPeople,
  onStartCall,
  onDeleteConversation,
  onBlocked,
  onToast,
  initialView = 'main',
  initialTab = 'media',
}) {
  const [info, setInfo] = useState(null);
  const [media, setMedia] = useState(null);
  const [view, setView] = useState(initialView);          // 'main' | 'media' | 'search'
  const [tab, setTab] = useState(initialTab);             // 'media' | 'links' | 'voice'
  const [muted, setMuted] = useState(false);
  const [query, setQuery] = useState('');
  const [actions, setActions] = useState(null);            // 'block' | 'report' | null

  useEffect(() => {
    let cancelled = false;
    setInfo(null); setMedia(null);
    (async () => {
      const r = await messageService.getThreadInfo(threadId);
      if (cancelled) return;
      if (r.success) { setInfo(r.data); setMuted(!!r.data.muted); }
      else onToast?.((r.error || 'COULDN’T LOAD THIS CHAT').toUpperCase());
    })();
    return () => { cancelled = true; };
  }, [threadId, onToast]);

  const loadMedia = useCallback(async () => {
    if (media) return;
    const r = await messageService.getThreadMedia(threadId);
    if (r.success) setMedia(r.data);
  }, [threadId, media]);
  useEffect(() => { if (view === 'media') loadMedia(); }, [view, loadMedia]);

  const openSub = (t) => { setTab(t); setView('media'); };

  const toggleMute = async () => {
    const next = !muted;
    setMuted(next);
    const r = await messageService.setThreadMuted(threadId, next);
    if (!r.success) { setMuted(!next); onToast?.('THAT DIDN’T SAVE'); } else onToast?.(next ? 'MUTED' : 'UNMUTED');
  };

  // The peek: the newest five items across every album / photo / clip in the thread, from
  // the loaded thread (so it needs no second request) — the full list comes from the API.
  const peek = useMemo(() => {
    const out = [];
    for (let k = messages.length - 1; k >= 0 && out.length < 5; k -= 1) {
      const m = messages[k];
      if (m.viewOnce || m._outbox) continue;
      if (m.kind === 'album') (m.albumItems || []).forEach((i, idx) => { if (out.length < 5 && i.status === 'ready') out.push({ msg: m, index: idx, url: i.thumbnailUrl || i.url, isVideo: i.kind === 'video', dur: i.durationSec }); });
      else if (m.kind === 'image' && m.imageUrl) out.push({ msg: m, index: 0, url: m.imageUrl, isVideo: false });
      else if (m.kind === 'video' && m.videoStatus === 'ready') out.push({ msg: m, index: 0, url: m.videoThumbnailUrl, isVideo: true, dur: m.videoDurationSec });
    }
    return out;
  }, [messages]);

  const hits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return messages.filter((m) => !m.system && !m.viewOnce && (previewText(m) || '').toLowerCase().includes(q)).slice(-40).reverse();
  }, [messages, query]);

  const first = (peer?.displayName || '').split(' ')[0].toUpperCase() || 'THEM';
  const live = reel?.portal ? portalStatus(reel.portal) : null;
  const callsVisible = !isGroup && !!peer?.userId;

  const header = (
    <div className="flex items-center flex-shrink-0" style={{ height: 64, gap: 8, padding: '0 12px' }}>
      {view !== 'main' && (
        <button type="button" onClick={() => { setView('main'); setQuery(''); }} aria-label="Back" style={{ width: 36, height: 36, border: 0, padding: 0, background: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="square"><path d="M19 12H5M11 6l-6 6 6 6" /></svg>
        </button>
      )}
      <span className="font-mono font-bold flex-1" style={{ fontSize: 12, letterSpacing: '.12em', paddingLeft: 6 }}>
        {view === 'media' ? 'MEDIA, LINKS + VOICE' : view === 'search' ? 'SEARCH THIS CHAT' : 'CHAT INFO'}
      </span>
      <button type="button" onClick={onClose} aria-label="Close chat info" style={{ width: 36, height: 36, borderRadius: '50%', border: 0, background: T.off, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="square"><path d="M6 6l12 12M18 6L6 18" /></svg>
      </button>
    </div>
  );

  const quick = [
    peer?.userId && { label: 'PROFILE', icon: IC.user, act: () => onOpenProfile?.(info?.peer?.href || peer?.href) },
    { label: 'SEARCH', icon: IC.search, act: () => setView('search') },
    onAddPeople && { label: 'ADD', icon: IC.add, act: (e) => onAddPeople(e.currentTarget.getBoundingClientRect()) },
    { label: muted ? 'MUTED' : 'MUTE', icon: IC.bell, on: muted, act: toggleMute },
    onToggleSecure && { label: 'SECURE', icon: IC.lock, on: isSecure, act: () => onToggleSecure(!isSecure) },
  ].filter(Boolean);

  const main = (
    <div className="flex-1 min-h-0 overflow-y-auto flex flex-col font-mono" style={{ padding: '4px 20px 28px', gap: 28 }}>
      {/* Identity */}
      <div className="flex flex-col items-center text-center" style={{ gap: 10 }}>
        <span style={{ position: 'relative', width: 96, height: 96, borderRadius: '50%', border: '3px solid transparent', background: fillFor('open'), boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {peer?.avatarUrl
            ? <img src={peer.avatarUrl} alt="" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }} />
            : <span className="font-bold" style={{ width: 80, height: 80, borderRadius: '50%', background: T.activeBadge, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>{(peer?.displayName || '?').charAt(0).toUpperCase()}</span>}
          {live?.live && (
            <span className="font-bold" style={{ position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%)', height: 18, padding: '0 7px', borderRadius: RADIUS.pill, background: T.live, color: T.white, fontSize: 8, letterSpacing: '.12em', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>PORTAL LIVE</span>
          )}
        </span>
        <span className="font-bold uppercase" style={{ marginTop: 6, fontSize: 16, letterSpacing: '.08em' }}>{peer?.displayName}</span>
        {(peer?.handle || info?.peer?.city) && (
          <span className="uppercase" style={{ fontSize: 10, letterSpacing: '.12em', color: T.grey500 }}>{[peer?.handle && `@${peer.handle}`, info?.peer?.city].filter(Boolean).join(' · ')}</span>
        )}
        {info?.peer?.vibeCheck && (
          <span style={{ fontFamily: "'Times New Roman', Times, serif", fontStyle: 'italic', fontSize: 17, lineHeight: 1.35, maxWidth: 300, textWrap: 'pretty' }}>“{info.peer.vibeCheck}”</span>
        )}
        <div className="flex flex-wrap justify-center" style={{ gap: 18, marginTop: 8 }}>
          {quick.map((q) => (
            <button key={q.label} type="button" onClick={q.act} data-testid={`chat-info-${q.label.toLowerCase()}`} className="flex flex-col items-center" style={{ gap: 7, border: 0, background: 'none', padding: 0, cursor: 'pointer' }}>
              <span style={{ width: 44, height: 44, borderRadius: '50%', border: '2px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', background: q.on ? fillFor('on') : T.off, color: q.on ? T.white : T.ink }}>
                <Icon d={q.icon} />
              </span>
              <span className="font-bold" style={{ fontSize: 9, letterSpacing: '.1em' }}>{q.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* UP NEXT — the plan this chat settled on; without one, the way to look for it. */}
      {(plan || (planAllowed && onFindPlan)) && (
        <section className="flex flex-col" style={{ gap: 10 }} data-testid="chat-info-plan">
          <span style={SECTION_LABEL}>UP NEXT · FROM THIS CHAT</span>
          {!plan && (
            <button type="button" onClick={() => { onFindPlan(); if (!docked) onClose?.(); }} className="hover:bg-black hover:text-white self-start" style={pill(false)}>FIND A PLAN</button>
          )}
          {plan && <div style={{ borderRadius: RADIUS.row, border: '2px solid transparent', background: fillFor('open'), padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="flex justify-between items-start" style={{ gap: 8 }}>
              <span style={{ fontFamily: BODY_FONT, fontSize: 16, fontWeight: 700, lineHeight: 1.3 }}>{plan.title || 'A plan'}</span>
              <span className="font-bold flex-shrink-0" style={{ height: 20, padding: '0 8px', borderRadius: RADIUS.pill, background: T.dayPill, fontSize: 8, letterSpacing: '.12em', display: 'flex', alignItems: 'center' }}>DRAFT</span>
            </div>
            {fmtWhen(plan.startLocal) && <span style={{ fontSize: 11, letterSpacing: '.08em' }}>{fmtWhen(plan.startLocal).toUpperCase()}</span>}
            <span style={{ fontFamily: BODY_FONT, fontSize: 13, lineHeight: 1.4, color: T.grey500 }}>{summaryLine(plan)}</span>
            <div className="flex" style={{ gap: 8 }}>
              <button type="button" onClick={onOpenPlanPortal} className="hover:bg-white hover:text-black" style={pill(true)}>OPEN THE PORTAL</button>
              {plan.sources?.find((s) => s.sourceMessageId) && (
                <button type="button" onClick={() => { const s = plan.sources.find((x) => x.sourceMessageId); onJumpTo?.(s.sourceMessageId); if (!docked) onClose?.(); }} className="hover:bg-black hover:text-white" style={pill(false)}>SEE IN CHAT</button>
              )}
            </div>
          </div>}
        </section>
      )}

      {/* MEDIA */}
      <section className="flex flex-col" style={{ gap: 10 }}>
        <div className="flex justify-between items-baseline">
          <span style={SECTION_LABEL}>MEDIA · {info ? info.mediaCount : '…'}</span>
          <button type="button" onClick={() => openSub('media')} className="font-bold underline" style={{ border: 0, background: 'none', padding: 0, fontSize: 10, letterSpacing: '.1em', cursor: 'pointer' }}>VIEW ALL</button>
        </div>
        {peek.length > 0 ? (
          <div data-testid="chat-info-peek" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gridTemplateRows: 'repeat(2, minmax(0, 1fr))', gap: 3, aspectRatio: '2 / 1', borderRadius: RADIUS.photo, overflow: 'hidden' }}>
            {peek.map((x, k) => (
              <button key={`${x.msg.id}:${x.index}`} type="button" onClick={() => onOpenMessageMedia?.(x.msg, x.index)} aria-label="Open" style={{ position: 'relative', padding: 0, border: 0, background: '#000', overflow: 'hidden', gridRow: k === 0 ? '1 / span 2' : 'auto', cursor: 'pointer' }}>
                {x.url && <img src={x.url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
                {x.isVideo && (
                  <span className="font-bold flex items-center" style={{ position: 'absolute', left: 6, bottom: 5, gap: 4, fontSize: 8, letterSpacing: '.1em', color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,.7)' }}>
                    <svg width="7" height="7" viewBox="0 0 24 24" fill="#fff"><path d="M6 3l15 9-15 9z" /></svg>{x.dur ? fmtDur(x.dur) : ''}
                  </span>
                )}
              </button>
            ))}
          </div>
        ) : (
          <span style={{ fontSize: 10, letterSpacing: '.1em', color: T.grey400 }}>NOTHING SHARED YET</span>
        )}
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          {info && info.linkCount > 0 && <button type="button" onClick={() => openSub('links')} className="font-bold" style={{ height: 30, padding: '0 12px', borderRadius: RADIUS.pill, border: '2px solid transparent', background: T.off, fontSize: 9, letterSpacing: '.1em', cursor: 'pointer' }}>{info.linkCount} {info.linkCount === 1 ? 'LINK' : 'LINKS'}</button>}
          {info && info.voiceCount > 0 && <button type="button" onClick={() => openSub('voice')} className="font-bold" style={{ height: 30, padding: '0 12px', borderRadius: RADIUS.pill, border: '2px solid transparent', background: T.off, fontSize: 9, letterSpacing: '.1em', cursor: 'pointer' }}>{info.voiceCount} {info.voiceCount === 1 ? 'VOICE NOTE' : 'VOICE NOTES'}</button>}
        </div>
      </section>

      {/* ROOMS YOU'VE SHARED */}
      {info?.sharedEvents?.length > 0 && (
        <section className="flex flex-col" style={{ gap: 4 }} data-testid="chat-info-rooms">
          <span style={{ ...SECTION_LABEL, marginBottom: 6 }}>ROOMS YOU’VE SHARED · {info.sharedEvents.length}</span>
          {info.sharedEvents.map((r) => (
            <button key={r.eventId} type="button" onClick={() => onOpenProfile?.(r.href)} className="flex items-center text-left hover:bg-[#F5F5F5]" style={{ gap: 12, padding: 8, margin: '0 -8px', border: 0, background: 'none', borderRadius: RADIUS.photo, cursor: 'pointer' }}>
              <span style={{ width: 52, height: 52, borderRadius: RADIUS.thumb, overflow: 'hidden', flexShrink: 0, background: T.activeBadge }}>
                {r.coverUrl && <img src={r.coverUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
              </span>
              <span className="min-w-0 flex flex-col" style={{ gap: 4 }}>
                <span className="truncate" style={{ fontFamily: BODY_FONT, fontSize: 14, lineHeight: 1.3 }}>{r.title}</span>
                <span style={{ fontSize: 9, letterSpacing: '.1em', color: T.grey500 }}>{r.meta}</span>
              </span>
            </button>
          ))}
        </section>
      )}

      {/* THEIR PORTAL */}
      {live?.live && reel?.portal && (
        <section className="flex flex-col" style={{ gap: 10 }}>
          <span style={SECTION_LABEL}>{first}’S PORTAL · LIVE</span>
          <button type="button" onClick={(e) => onOpenReel?.(peer.userId, e.currentTarget.getBoundingClientRect())} className="flex items-center text-left" style={{ gap: 12, padding: 10, borderRadius: RADIUS.row, border: 0, background: T.ink, color: T.white, cursor: 'pointer' }}>
            <span style={{ width: 54, height: 72, borderRadius: RADIUS.thumb, overflow: 'hidden', flexShrink: 0, position: 'relative', background: '#222' }}>
              {reel.portal.thumbnailUrl && <img src={reel.portal.thumbnailUrl} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
            </span>
            <span className="min-w-0 flex flex-col" style={{ gap: 6 }}>
              {reel.portal.text && <span style={{ fontFamily: BODY_FONT, fontSize: 14, lineHeight: 1.3 }}>{reel.portal.text}</span>}
              <span className="font-bold" style={{ fontSize: 9, letterSpacing: '.12em', color: T.live }}>{live.label}{reel.portal.acceptedCount > 0 ? ` · ${reel.portal.acceptedCount} DOWN` : ''}</span>
            </span>
          </button>
        </section>
      )}

      {/* CALLS */}
      {callsVisible && (
        <section className="flex flex-col" style={{ gap: 4 }} data-testid="chat-info-calls">
          <span style={{ ...SECTION_LABEL, marginBottom: 6 }}>CALLS</span>
          {(info?.calls || []).map((m) => {
            const v = callRecordView(m, viewerUserId, { peerName: peer?.displayName });
            const colour = v.missed ? T.danger : T.ink;
            return (
              <div key={m.id} className="flex items-center" style={{ gap: 12, padding: '6px 0' }}>
                <span style={{ width: 36, height: 36, borderRadius: '50%', background: T.off, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: colour }}>
                  <CallIcon name={v.missed ? 'missed' : m.call?.video ? 'video' : 'phone'} size={15} />
                </span>
                <span className="flex-1 flex flex-col" style={{ gap: 3 }}>
                  <span className="font-bold" style={{ fontSize: 11, letterSpacing: '.1em', color: colour }}>{v.label}{m.call?.status === 'done' ? '' : ''}</span>
                  <span style={{ fontSize: 9, letterSpacing: '.1em', color: T.grey500 }}>{formatDay(m.createdAt)}, {formatTime(m.createdAt)} · {m.senderId === viewerUserId ? 'YOU CALLED' : `${first} CALLED`}</span>
                </span>
              </div>
            );
          })}
          {onStartCall && (
            <div className="flex" style={{ gap: 8, marginTop: 6 }}>
              <button type="button" onClick={(e) => onStartCall(false, e)} className="hover:bg-black hover:text-white" style={pill(false)}>VOICE CALL</button>
              <button type="button" onClick={(e) => onStartCall(true, e)} className="hover:bg-black hover:text-white" style={pill(false)}>VIDEO CALL</button>
            </div>
          )}
        </section>
      )}

      {/* THIS CHAT */}
      <section className="flex flex-col relative" data-testid="chat-info-settings">
        <span style={{ ...SECTION_LABEL, marginBottom: 6 }}>THIS CHAT</span>
        {planAllowed && onTogglePlan && (
          <Row icon={IC.calendar} label="PLAN DETECTION" value={planEnabled ? 'ON' : 'OFF'} onClick={() => onTogglePlan(!planEnabled)} />
        )}
        <Row icon={IC.eye} label="VIEW LIMIT FOR NEW MEDIA" value="UNLIMITED" onClick={() => onToast?.('SET PER MESSAGE IN THE COMPOSER')} />
        {peer?.userId && <Row icon={IC.block} label={`BLOCK ${first}`} onClick={() => setActions('block')} />}
        {peer?.userId && <Row icon={IC.flag} label="REPORT" onClick={() => setActions('report')} />}
        <Row icon={IC.trash} label="DELETE CONVERSATION" danger onClick={onDeleteConversation} />
        {peer?.userId && (
          <UserActionsMenu
            open={!!actions}
            onClose={() => setActions(null)}
            userId={peer.userId}
            displayName={peer.displayName}
            anchor="right"
            onBlocked={(id) => { setActions(null); onBlocked?.(id); }}
          />
        )}
      </section>
    </div>
  );

  const mediaView = (
    <>
      <div className="flex flex-shrink-0" style={{ padding: '0 16px 12px', gap: 6 }}>
        {[['media', `MEDIA · ${info?.mediaCount ?? media?.media.reduce((a, g) => a + g.items.length, 0) ?? 0}`], ['links', `LINKS · ${info?.linkCount ?? media?.links.length ?? 0}`], ['voice', `VOICE · ${info?.voiceCount ?? media?.voice.length ?? 0}`]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} className="font-mono font-bold" style={{ height: 32, padding: '0 14px', borderRadius: RADIUS.pill, border: '2px solid transparent', fontSize: 10, letterSpacing: '.08em', background: tab === k ? fillFor('on') : T.off, color: tab === k ? T.white : T.ink, cursor: 'pointer' }}>{l}</button>
        ))}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col font-mono" style={{ padding: '0 16px 24px', gap: 18 }}>
        {!media && <span style={{ fontSize: 10, letterSpacing: '.1em', color: T.grey400 }}>…</span>}
        {media && tab === 'media' && media.media.map((g) => (
          <div key={g.messageId} className="flex flex-col" style={{ gap: 8 }}>
            <div className="flex justify-between font-bold" style={{ fontSize: 9, letterSpacing: '.12em', color: T.grey500 }}>
              <span>{formatDay(g.createdAt)} · {formatTime(g.createdAt)}{g.eventTitle ? ` · ${g.eventTitle.toUpperCase()}` : ''}</span>
              <span>{g.senderId === viewerUserId ? 'YOU' : first}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 3, borderRadius: RADIUS.thumb, overflow: 'hidden' }}>
              {g.items.map((x, idx) => (
                <button key={x.id} type="button" onClick={() => onOpenAlbum?.(g, idx)} aria-label="Open" style={{ position: 'relative', aspectRatio: '1', padding: 0, border: 0, background: '#000', overflow: 'hidden', cursor: 'pointer' }}>
                  {(x.thumbnailUrl || x.url) && <img src={x.thumbnailUrl || x.url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
                  {x.kind === 'video' && (
                    <span className="font-bold flex items-center" style={{ position: 'absolute', left: 6, bottom: 5, gap: 4, fontSize: 8, letterSpacing: '.1em', color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,.7)' }}>
                      <svg width="7" height="7" viewBox="0 0 24 24" fill="#fff"><path d="M6 3l15 9-15 9z" /></svg>{x.durationSec ? fmtDur(x.durationSec) : ''}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
        {media && tab === 'media' && media.media.length === 0 && <span style={{ fontSize: 10, letterSpacing: '.1em', color: T.grey400 }}>NO PHOTOS OR CLIPS YET</span>}
        {media && tab === 'links' && media.links.map((l) => (
          <a key={l.id} href={l.linkUrl} target="_blank" rel="noreferrer" className="flex items-center" style={{ gap: 12, padding: 10, borderRadius: RADIUS.row, background: T.canvas, color: 'inherit', textDecoration: 'none' }}>
            <span style={{ width: 56, height: 56, borderRadius: RADIUS.small, overflow: 'hidden', flexShrink: 0, background: T.activeBadge }}>
              {l.linkImageUrl && <img src={l.linkImageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
            </span>
            <span className="min-w-0 flex flex-col" style={{ gap: 3 }}>
              <span className="font-bold truncate" style={{ fontSize: 9, letterSpacing: '.12em', color: T.grey500 }}>{(l.linkSiteName || (() => { try { return new URL(l.linkUrl).host; } catch { return ''; } })()).toUpperCase()}</span>
              <span className="truncate" style={{ fontFamily: BODY_FONT, fontSize: 14, fontWeight: 700, lineHeight: 1.3 }}>{l.linkTitle || l.linkUrl}</span>
              <span style={{ fontSize: 9, letterSpacing: '.1em', color: T.grey500 }}>{l.senderId === viewerUserId ? 'YOU' : first} · {formatDay(l.createdAt)}</span>
            </span>
          </a>
        ))}
        {media && tab === 'links' && media.links.length === 0 && <span style={{ fontSize: 10, letterSpacing: '.1em', color: T.grey400 }}>NO LINKS YET</span>}
        {media && tab === 'voice' && media.voice.length > 0 && onSearchVoiceNotes && (
          <button type="button" onClick={onSearchVoiceNotes} data-testid="chat-info-search-notes" className="flex items-center font-bold hover:bg-black hover:text-white" style={{ gap: 8, height: 36, padding: '0 14px', borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, background: T.white, color: T.ink, fontSize: 9, letterSpacing: '.12em', cursor: 'pointer', alignSelf: 'flex-start', transition: 'background-color 200ms, color 200ms' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="square" aria-hidden><circle cx="10.5" cy="10.5" r="6.5" /><path d="M15.5 15.5L21 21" /></svg>
            SEARCH THE NOTES ON THESE
          </button>
        )}
        {media && tab === 'voice' && media.voice.map((v) => (
          <button key={v.id} type="button" onClick={() => { onJumpTo?.(v.id); if (!docked) onClose?.(); }} className="flex items-center text-left" style={{ gap: 12, padding: 12, borderRadius: RADIUS.row, background: T.canvas, border: 0, cursor: 'pointer' }}>
            <span style={{ width: 40, height: 40, borderRadius: '50%', background: T.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><path d="M6 3l15 9-15 9z" /></svg></span>
            <span className="flex-1 flex flex-col" style={{ gap: 3 }}>
              <span className="font-bold" style={{ fontSize: 11, letterSpacing: '.1em' }}>{v.senderId === viewerUserId ? 'YOU' : first} · {fmtDur(v.audioDurationSec)}</span>
              <span style={{ fontSize: 9, letterSpacing: '.1em', color: T.grey500 }}>{formatDay(v.createdAt)}, {formatTime(v.createdAt)}</span>
            </span>
          </button>
        ))}
        {media && tab === 'voice' && media.voice.length === 0 && <span style={{ fontSize: 10, letterSpacing: '.1em', color: T.grey400 }}>NO VOICE NOTES YET</span>}
      </div>
    </>
  );

  const searchView = (
    <div className="flex-1 min-h-0 flex flex-col font-mono" style={{ padding: '0 16px 24px', gap: 12 }}>
      <input
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search this chat…"
        aria-label="Search this chat"
        style={{ height: 40, borderRadius: RADIUS.pill, border: '2px solid transparent', background: T.off, padding: '0 16px', fontFamily: BODY_FONT, fontSize: 15, outline: 'none' }}
      />
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col" style={{ gap: 4 }}>
        {query && hits.length === 0 && <span style={{ fontSize: 10, letterSpacing: '.1em', color: T.grey400, padding: '8px 2px' }}>NOTHING SAID LIKE THAT</span>}
        {hits.map((m) => (
          <button key={m.id} type="button" onClick={() => { onJumpTo?.(m.id); if (!docked) onClose?.(); }} className="text-left hover:bg-[#F5F5F5]" style={{ padding: '8px 10px', border: 0, background: 'none', borderRadius: RADIUS.photo, cursor: 'pointer' }}>
            <span className="block font-bold" style={{ fontSize: 9, letterSpacing: '.1em', color: T.grey500 }}>{m.senderId === viewerUserId ? 'YOU' : (m.senderDisplayName || first).toUpperCase()} · {formatDay(m.createdAt)} {formatTime(m.createdAt)}</span>
            <span className="block truncate" style={{ fontFamily: BODY_FONT, fontSize: 14, marginTop: 2 }}>{previewText(m)}</span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <aside
      data-testid="chat-info-sheet"
      data-docked={docked ? 'true' : 'false'}
      aria-label="Chat info"
      className="flex flex-col"
      style={{ width: SHEET_WIDTH, maxWidth: '100%', flexShrink: 0, background: T.white, zIndex: 41, position: docked ? 'relative' : 'absolute', right: 0, top: 0, bottom: 0, borderLeft: `2px solid ${T.off}`, boxSizing: 'border-box', paddingTop: docked ? 0 : 'env(safe-area-inset-top, 0px)' }}
    >
      {header}
      {view === 'main' ? main : view === 'media' ? mediaView : searchView}
    </aside>
  );
}

function Row({ icon, label, value, danger = false, onClick }) {
  return (
    <button type="button" onClick={onClick} className="flex items-center text-left font-mono" style={{ gap: 14, padding: '13px 0', border: 0, borderTop: `2px solid ${T.off}`, background: 'none', color: danger ? T.danger : T.ink, cursor: 'pointer' }}>
      <Icon d={icon} />
      <span className="flex-1 font-bold" style={{ fontSize: 11, letterSpacing: '.1em' }}>{label}</span>
      {value && <span style={{ fontSize: 10, letterSpacing: '.1em', color: T.grey500 }}>{value}</span>}
    </button>
  );
}

/** The secure-chat lock glyph beside a name in the header: 12px, stroke 2.5. */
export function HeaderLock() {
  return <LockIcon size={12} strokeWidth={2.5} />;
}
